import { Label } from "@/components/ui/label";
import type { ReactNode } from "react";
import { CustomFieldControl } from "./CustomFieldControl";
import { FieldDescription } from "./FieldDescription";
import { ReadOnlyField } from "./ReadOnlyField";
import { ReadOnlyValue } from "./ReadOnlyValue";
import type { CustomFieldsSchema, CustomFieldValues } from "@forgepoint/shared";
import { getRequiredCustomFieldNames } from "@forgepoint/shared";
import { cn } from "@/lib/utils";

/** Props for the CustomFieldsSection component. */
interface CustomFieldsSectionProps {
  schema: CustomFieldsSchema;
  values: CustomFieldValues;
  readOnly: boolean;
  onChange: (fieldName: string, value: CustomFieldValues[string]) => void;
  /** When true, renders with `display: contents` so items flow into a parent grid. */
  inline?: boolean;
  /** Uses tighter spacing and smaller labels for dense detail pages. */
  compact?: boolean;
  /** Renders fields as dense label/control rows for item-style detail forms. */
  layout?: "grid" | "rows";
  additionalRequiredNames?: string[];
  getFieldClassName?: (fieldName: string) => string | undefined;
}

function CustomFieldRow({
  label,
  htmlFor,
  description,
  required,
  className,
  children,
}: {
  label: string;
  htmlFor?: string;
  description?: string;
  required: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn("py-2", className)}
      data-field-anchor={`customFields.${label}`}
      data-custom-field={label}
    >
      {/* The row wraps rather than squeezes, so a narrow container stacks the
          control under its label instead of pushing it past the edge. The
          description rides in the control's box to stay aligned either way. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
        <Label
          htmlFor={htmlFor}
          className="min-w-0 grow basis-40 text-xs font-medium leading-tight text-foreground"
        >
          {label}
          {required && <span className="ml-0.5 text-destructive">*</span>}
        </Label>
        <div className="min-w-[120px] shrink basis-[240px]">
          {children}
          {description && (
            <div className="mt-1">
              <FieldDescription description={description} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getCustomFieldInputId(fieldName: string) {
  return `custom-field-${fieldName.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

/**
 * Renders custom fields as inline grid items using `display: contents`,
 * so they flow directly into the parent grid alongside built-in fields.
 * In read-only mode, displays values matching the parent's label/value style.
 * In edit mode, renders appropriate input controls per field type.
 * @param schema - The custom fields JSON Schema defining available fields
 * @param values - Current field values keyed by field name
 * @param readOnly - Whether the section is in read-only display mode
 * @param onChange - Callback when a field value changes
 * @param inline - When true, renders with `display: contents` to flow into a parent grid
 * @param compact - Uses tighter spacing and smaller labels
 * @param layout - Renders a grid of fields or dense label/control rows
 * @param additionalRequiredNames - Field names to mark required on top of the schema's required list
 * @param getFieldClassName - Returns extra classes for an editable field's wrapper
 * @returns The custom fields, or null when the schema has none
 */
export function CustomFieldsSection({
  schema,
  values,
  readOnly,
  onChange,
  inline = false,
  compact = false,
  layout = "grid",
  additionalRequiredNames = [],
  getFieldClassName,
}: CustomFieldsSectionProps) {
  const baseKeys = schema.propertyOrder ?? Object.keys(schema.properties);
  const orderedKeySet = new Set(baseKeys);
  const remainingKeys = Object.keys(schema.properties).filter(
    (k) => !orderedKeySet.has(k),
  );
  const allKeys = [...baseKeys, ...remainingKeys];
  const requiredNames = new Set([
    ...getRequiredCustomFieldNames(schema),
    ...additionalRequiredNames,
  ]);

  if (allKeys.length === 0) {
    return null;
  }

  if (layout === "rows") {
    const rowItems = allKeys.map((fieldName) => {
      const prop = schema.properties[fieldName];
      if (!prop) {
        return null;
      }

      if (readOnly || prop.readOnly === true) {
        return (
          <CustomFieldRow
            key={fieldName}
            label={fieldName}
            description={prop.description}
            required={requiredNames.has(fieldName)}
          >
            <ReadOnlyValue
              value={values[fieldName] ?? null}
              format={prop.format}
              type={prop.type[0]}
              inline={false}
            />
          </CustomFieldRow>
        );
      }

      const fieldId = getCustomFieldInputId(fieldName);
      return (
        <CustomFieldRow
          key={fieldName}
          label={fieldName}
          htmlFor={fieldId}
          description={prop.description}
          required={requiredNames.has(fieldName)}
          className={getFieldClassName?.(fieldName)}
        >
          <CustomFieldControl
            fieldName={fieldName}
            prop={prop}
            value={values[fieldName]}
            onChange={onChange}
            size="sm"
            inputId={fieldId}
          />
        </CustomFieldRow>
      );
    });

    return <div className="divide-y rounded-md border px-3">{rowItems}</div>;
  }

  const fieldItems = allKeys.map((fieldName) => {
    const prop = schema.properties[fieldName];
    if (!prop) {
      return null;
    }

    if (readOnly || prop.readOnly === true) {
      return (
        <ReadOnlyField
          key={fieldName}
          fieldName={fieldName}
          value={values[fieldName] ?? null}
          format={prop.format}
          type={prop.type[0]}
          inline={inline}
          description={prop.description}
          compact={compact}
          required={requiredNames.has(fieldName)}
        />
      );
    }

    return (
      <div
        key={fieldName}
        className={cn(
          compact ? "space-y-1" : "space-y-1.5",
          getFieldClassName?.(fieldName),
        )}
        data-custom-field={fieldName}
      >
        <Label
          className={cn(
            compact
              ? "text-xs font-medium text-foreground"
              : "text-sm text-muted-foreground",
          )}
        >
          {fieldName}
          {requiredNames.has(fieldName) && (
            <span className="ml-0.5 text-destructive">*</span>
          )}
        </Label>
        <CustomFieldControl
          fieldName={fieldName}
          prop={prop}
          value={values[fieldName]}
          onChange={onChange}
          size={compact ? "sm" : "default"}
        />
        <FieldDescription description={prop.description} />
      </div>
    );
  });

  if (inline) {
    return <div className="contents">{fieldItems}</div>;
  }

  if (compact) {
    return (
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        {fieldItems}
      </div>
    );
  }

  return (
    <div className="border-t pt-5">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        Custom Fields
      </h3>
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
        {fieldItems}
      </div>
    </div>
  );
}
